import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { TemplateDetailComponent } from '../template-detail/template-detail.component';
import { DeleteDialog } from '../../dialogs/delete.component';
import { TemplateService } from '../template.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderComponent } from "src/app/shared/loader/loader.component";
import { EmptyStateComponent } from 'src/app/shared/empty-state/empty-state.component';
import { ITemplate, ITemplateRequest } from '../template.interface';
import { TemplateType, TemplateTypeLabels } from 'src/app/common/enums/template-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-template-list',
  standalone: true,
  imports: [
    MaterialModule,
    TablerIconsModule,
    CommonModule,
    TranslateModule,
    LoaderComponent,
    EmptyStateComponent,
    ReactiveFormsModule
  ],
  providers: [
    DatePipe
  ],
  templateUrl: './template-list.component.html',
  styleUrl: './template-list.component.scss'
})
export class TemplateListComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatTable, { static: true }) table!: MatTable<any>;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<ITemplate>([]);
  templateCount = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  pagination: PaginationResult = {
    currentPage: 0,
    pageCount: 0,
    rowCount: 0,
    searchProperty: '',
    searchValue: '',
    searchComapreOption: '',
    pageNumber: 0,
    pageSize: 100
  };

  templatesData: ITemplate[] = [];
  isLoading = false;
  searchTemplate: FormControl = new FormControl('');

  selectedTemplateType: TemplateType | null = null;
  TemplateType = TemplateType;
  TemplateTypeLabels = TemplateTypeLabels;
  templateTypeList = Object.values(TemplateType).filter(x => typeof x === 'number') as TemplateType[];

  // Dialog mode properties
  isDialogMode = false;
  dialogTemplateType: TemplateType | undefined;

  private _unsubscribeAll = new Subject<void>();

  displayedColumns: string[] = [
    'templateName', 'description', 'action'
  ];

  constructor(
    public dialog: MatDialog,
    public datePipe: DatePipe,
    private _templateService: TemplateService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Optional() private dialogRef: MatDialogRef<TemplateListComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: { templateType: TemplateType }
  ) {
    // Check if component is opened as dialog
    if (this.dialogRef && this.dialogData) {
      this.isDialogMode = true;
      this.dialogTemplateType = this.dialogData.templateType;
      this.selectedTemplateType = this.dialogData.templateType;
    }
  }

  ngOnInit(): void {

    this._templateService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination) => {
        if (pagination) {
          this.pagination = pagination;
          this.templateCount = pagination.rowCount;
        }
      });

    this._templateService.templates$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.templatesData = data;
        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
      });

    // Search subscription with debounce
    this.searchTemplate.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        const searchValue = query?.trim() || '';
        if (this.pagination) {
          this.pagination.currentPage = 0;
          this.pagination.pageNumber = 0;
        }
        return this.loadTemplatesWithSearch(searchValue).pipe(
          finalize(() => (this.isLoading = false)),
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.templateDetails;
        this.templateCount = response.result.pagination.rowCount;
        this.pagination = response.result.pagination;
      }
    });

    this.loadTemplates();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadTemplates(): void {
    const searchValue = this.searchTemplate?.value?.trim() || '';
    this.loadTemplatesWithSearch(searchValue)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          if (response?.result) {
            this.dataSource.data = response.result.templateDetails;
            this.templateCount = response.result.pagination.rowCount;
            this.pagination = response.result.pagination;
          }
        },
        error: (error) => {
          console.error('Error loading templates:', error);
        }
      });
  }

  private loadTemplatesWithSearch(searchValue: string = ''): Observable<BaseRespone<{ pagination: PaginationResult; templateDetails: ITemplate[] }>> {
    const request: ITemplateRequest = {
      pageSize: this.pagination?.pageSize ?? 100,
      currentPage: this.pagination?.currentPage ?? 0,
      pageNumber: this.pagination?.currentPage ?? 0,
      templateType: this.selectedTemplateType ?? undefined,
      searchValue: searchValue || undefined,
      searchProperty: searchValue ? 'templateName' : undefined,
      searchComapreOption: searchValue ? 'contains' : undefined
    };

    return this._templateService.getAllTemplates(request);
  }

  onTemplateTypeClick(templateType: TemplateType | null): void {
    this.selectedTemplateType = templateType;
    this.searchTemplate.setValue('', { emitEvent: false });
    if (this.pagination) {
      this.pagination.currentPage = 0;
      this.pagination.pageNumber = 0;
    }
    this.loadTemplates();
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(TemplateDetailComponent, {
      height: 'auto',
      minWidth: '40vw',
      disableClose: true,
      data: {
        templateType: this.selectedTemplateType
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Clear search to show all templates including the newly added one
        this.searchTemplate.setValue('', { emitEvent: false });
        if (this.pagination) {
          this.pagination.currentPage = 0;
          this.pagination.pageNumber = 0;
        }
        this.loadTemplates();
      }
    });
  }

  editOpenDialog(template: ITemplate): void {
    const dialogRef = this.dialog.open(TemplateDetailComponent, {
      height: 'auto',
      minWidth: '40vw',
      disableClose: true,
      data: {
        template: template,
        isEdit: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Preserve search when editing, but reload templates
        this.loadTemplates();
      }
    });
  }

  deleteOpenDialog(id: number): void {
    this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: `${this.translate.instant('DELETE_DIALOG')} ${this.translate.instant('ENTITY.TEMPLATE')} ?` }
    }).afterClosed().subscribe(result => {
      if (result === "confirm") {
        this._templateService.deleteTemplate(id)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(response => {
            if (response.isSuccess) {
              // Preserve search when deleting, but reload templates
              this.loadTemplates();
              this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.TEMPLATE')));
            } else {
              this.toastr.error(Object.values(response.errors).flat().join("\n"));
            }
          });
      }
    });
  }

  isTemplateTypeActive(templateType: TemplateType | null): boolean {
    return this.selectedTemplateType === templateType;
  }

  getTemplateTypeLabel(templateType: TemplateType): string {
    return TemplateTypeLabels[templateType];
  }

  onSelectTemplate(template: ITemplate): void {
    if (this.isDialogMode && this.dialogRef) {
      // Return the selected template to the parent component
      this.dialogRef.close({ selected: true, template: template });
    }
  }

  onCloseDialog(): void {
    if (this.isDialogMode && this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
